import {useCompany, useCreditDecisionMatrix} from '@api'
import {Country, FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {FormFieldItem, isCompanyUser, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export function useCreditDecisionViewModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const navigate = useViewNavigate()
  const {t} = useTranslation()

  const {data: creditDecisionData} = useCreditDecisionMatrix()

  const {data: companyData} = useCompany()
  //data.companyId

  const notRequiredFields: string[] = useMemo(() => {
    const companyCountry = companyData?.country
    switch (companyCountry) {
      case Country.SWEDEN:
        return []
      case Country.NORWAY:
        return []
      case Country.DENMARK:
        return []
      default:
        return []
    }
  }, [creditDecisionData, companyData])

  /**
   * Edit button is only available to Administrator users
   */
  const isEditButtonVisible = !isCompanyUser()

  useEffect(
    () =>
      setTitleLinks([
        {
          title: t(TranslationKey['Admin panel']),
          link: NavigationPathKey.ADMIN_PANEL,
        },
        {title: t(TranslationKey.Contracts), link: NavigationPathKey.CONTRACT_DASHBOARD},

        {title: t(TranslationKey['Credit Decision Configuration'])},
      ]),
    [],
  )

  /**
   * Contract Type form fields configuration
   */
  const creditDecisionFormFields: FormFieldItem[] = useMemo(
    () =>
      [
        {
          fieldType: FormFieldType.LABEL,
          label: t(TranslationKey.Company),
          text: companyData?.name || '-',
        },
        {
          fieldType: FormFieldType.LABEL,
          label: t(TranslationKey['Allowed credit rating']),
          text: creditDecisionData?.allowedCreditRating || '-',
        },
        {
          fieldType: FormFieldType.LABEL,
          label: t(TranslationKey['Minimum income']),
          text: creditDecisionData?.minimumIncome ? String(creditDecisionData?.minimumIncome) : '0',
        },
        {
          fieldType: FormFieldType.LABEL,
          label: t(TranslationKey['Max existing debt']),
          text: creditDecisionData?.maxExistingDebt ? String(creditDecisionData?.maxExistingDebt) : '0',
        },
        {
          fieldType: FormFieldType.LABEL,
          label: t(TranslationKey['Require no active collections']),
          text: creditDecisionData?.requireNoActiveCollections ? t(TranslationKey.Yes) : t(TranslationKey.No),
        },
      ].filter(item => !notRequiredFields.includes(item.label)),
    [notRequiredFields],
  )

  /**
   * A function handling the Back button click
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.ADMIN_PANEL)
  }

  /**
   * A function handling Edit button click
   */
  const onPressEdit = (): void => {
    navigate(NavigationPathKey.CREDIT_DECISION_EDIT)
  }

  return {
    navigate,
    onPressEdit,
    onPressBack,
    t,
    isEditButtonVisible,
    creditDecisionData,
    creditDecisionFormFields,
  }
}
